import React, { useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, IconButton, Theme, Typography } from "@mui/material";
import FiberNewIcon from "@mui/icons-material/FiberNew";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import BuildIcon from "@mui/icons-material/Build";
import WarningIcon from "@mui/icons-material/Warning";
import NotesIcon from "@mui/icons-material/Notes";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

/* region Languages */
import { CustomerMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";
/* endregion Languages */

import { ChangeKind, ChangelogItem, ChangelogVersion } from "../changelog";

/** Fills the <NAME> placeholders of a translated text */
export const fill = (template: string, values: Record<string, string | number>): string => Object.entries(values)
  .reduce((text, [name, value]) => text.split(`<${name}>`).join(String(value)), template);

/** Colour, icon and texts of each kind of change, shared by the sidebar counters */
export const KIND_STYLE: Record<ChangeKind, {
  color: string; background: string; Icon: typeof FiberNewIcon; label: CustomerMessages; one: CustomerMessages; many: CustomerMessages
}> = {
  added: {
    color: "#1b5fa8",
    background: "#e5eef9",
    Icon: FiberNewIcon,
    label: CustomerMessages.docsKindAddedLabel,
    one: CustomerMessages.docsKindAddedOne,
    many: CustomerMessages.docsKindAddedMany
  },
  improved: {
    color: "#9a5300",
    background: "#fbefdc",
    Icon: TrendingUpIcon,
    label: CustomerMessages.docsKindImprovedLabel,
    one: CustomerMessages.docsKindImprovedOne,
    many: CustomerMessages.docsKindImprovedMany
  },
  fixed: {
    color: "#17784a",
    background: "#e1f3e9",
    Icon: BuildIcon,
    label: CustomerMessages.docsKindFixedLabel,
    one: CustomerMessages.docsKindFixedOne,
    many: CustomerMessages.docsKindFixedMany
  },
  breaking: {
    color: "#b42318",
    background: "#fdecea",
    Icon: WarningIcon,
    label: CustomerMessages.docsKindBreakingLabel,
    one: CustomerMessages.docsKindBreakingOne,
    many: CustomerMessages.docsKindBreakingMany
  },
  other: {
    color: "#5b6878",
    background: "#eceff3",
    Icon: NotesIcon,
    label: CustomerMessages.docsKindOtherLabel,
    one: CustomerMessages.docsKindOtherOne,
    many: CustomerMessages.docsKindOtherMany
  }
};

/** Display order of the kinds (KPIs, sidebar counters) */
export const KIND_ORDER = Object.keys(KIND_STYLE) as ChangeKind[];

/** Longest summary shown in the table before the text is cut */
const SUMMARY_LENGTH = 120;

/**
 * Splits a text into sentences without breaking version numbers ("1.20.0") or abbreviations:
 * a new sentence starts only after a full stop followed by a capital letter.
 * @param text Full text of a CHANGELOG item
 */
export const splitSentences = (text: string): string[] => text
  .split(/(?<=[.!?])\s+(?=[A-ZÁÉÍÓÚÂÊÔÃÕÇ*`“"(])/)
  .filter(Boolean);

/**
 * Renders the inline markdown the CHANGELOG uses (**bold**, *italic*, `code`) as React nodes, without injecting HTML.
 * @param text One bullet of the CHANGELOG
 */
export const renderInline = (text: string): React.ReactNode[] => {
  let offset = 0;

  return text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g).filter(Boolean).map((part) => {
    const key = `${offset}`;

    offset += part.length;

    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) return <strong key={key}>{part.slice(2, -2)}</strong>;
    if (part.startsWith("*") && part.endsWith("*") && part.length > 2) return <em key={key}>{part.slice(1, -1)}</em>;
    if (part.startsWith("`") && part.endsWith("`") && part.length > 2) return <code key={key}>{part.slice(1, -1)}</code>;

    return <React.Fragment key={key}>{part}</React.Fragment>;
  });
};

/**
 * Short version of an item for the table: its first sentence, cut at a word when still too long.
 * @param text Full text of the item
 */
export const summarize = (text: string): string => {
  const plain = text.replace(/\*\*|`/g, "").replace(/\*/g, "");
  const first = plain.split(/(?<=[.!?])\s+/)[0];

  if (first.length <= SUMMARY_LENGTH) return first;

  const cut = first.slice(0, SUMMARY_LENGTH);

  return `${cut.slice(0, cut.lastIndexOf(" "))}…`;
};

const kpiSx = (color: string) => (theme: Theme) => ({
  display: "grid",
  gridTemplateColumns: "auto 1fr",
  columnGap: 1.5,
  alignItems: "center",
  textAlign: "left",
  font: "inherit",
  cursor: "pointer",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderTop: `4px solid ${color}`,
  borderRadius: "10px",
  px: 2,
  py: 1.5,
  boxShadow: "0 1px 2px rgba(0,0,0,.04)",
  transition: "transform .15s",
  "&:hover": { transform: "translateY(-1px)" }
});

const iconBoxSx = (color: string, background: string, size: number, radius: string) => ({
  width: size,
  height: size,
  borderRadius: radius,
  display: "grid",
  placeItems: "center",
  color,
  backgroundColor: background,
  flex: "none"
});

const tableSx = (theme: Theme) => ({
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: 0,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "10px",
  overflow: "hidden",
  fontSize: 14,
  "& th": {
    textAlign: "left",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: ".06em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    px: 1.5,
    py: 1,
    backgroundColor: theme.palette.action.hover,
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  "& td": { px: 1.5, py: 1.25, borderBottom: `1px solid ${theme.palette.divider}`, verticalAlign: "middle" },
  "& tbody tr": { cursor: "pointer" },
  "& tbody tr:hover td": { backgroundColor: theme.palette.action.hover },
  "& tbody tr:last-child td": { borderBottom: 0 }
});

const chipSx = (color: string, background: string) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  borderRadius: "14px",
  px: 1.1,
  py: 0.25,
  fontSize: 12,
  fontWeight: 600,
  color,
  backgroundColor: background
});

const leadSx = (color: string, background: string) => ({
  fontSize: 15.5,
  lineHeight: 1.55,
  px: 1.75,
  py: 1.5,
  borderRadius: "8px",
  borderLeft: `4px solid ${color}`,
  backgroundColor: background
});

const stepSx = (theme: Theme) => ({
  display: "flex",
  gap: 1.25,
  alignItems: "flex-start",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "8px",
  px: 1.5,
  py: 1.25,
  fontSize: 14,
  lineHeight: 1.5
});

interface Row {
  kind: ChangeKind;
  item: ChangelogItem;
}

interface ChangelogVersionViewProps {
  entry: ChangelogVersion;
  /** Buttons shown next to the version title (Novidades em detalhe, Resumo de versões) */
  actions?: React.ReactNode;
}

/** One CHANGELOG version as one compact table per kind of change; each row opens a small dialog with the full text */
const ChangelogVersionView: React.FC<ChangelogVersionViewProps> = ({ entry, actions }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<Row | null>(null);
  const rows: Row[] = entry.sections.flatMap((section) => section.items.map((item) => ({ kind: section.kind, item })));
  const kinds = KIND_ORDER.filter((kind) => rows.some((row) => row.kind === kind));
  const versionLabel = fill(t(CustomerMessages.docsVersion), { VERSION: entry.version });

  return (
    <div>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 2.5 }}>
        <div>
          <Typography component="h1" sx={{ fontSize: 26, fontWeight: 600, lineHeight: 1.2 }}>{versionLabel}</Typography>
          {entry.date && <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.5 }}>{entry.date}</Typography>}
        </div>
        {actions && <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>{actions}</Box>}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 1.5, mb: 3 }}>
        {kinds.map((kind) => {
          const { color, background, Icon, one, many } = KIND_STYLE[kind];
          const count = rows.filter((row) => row.kind === kind).length;

          return (
            <Box
              key={kind}
              component="button"
              type="button"
              sx={kpiSx(color)}
              onClick={() => document.getElementById(`changelog-${kind}`)?.scrollIntoView({ behavior: "smooth", block: "start" })}
            >
              <Box sx={{ ...iconBoxSx(color, background, 40, "10px"), gridRow: "1 / 3" }}><Icon sx={{ fontSize: 22 }} /></Box>
              <Box component="span" sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1.1, color }}>{count}</Box>
              <Box component="span" sx={{ fontSize: 13, color: "text.secondary" }}>{t(count === 1 ? one : many)}</Box>
            </Box>
          );
        })}
      </Box>

      {entry.sections.filter((section) => section.items.length > 0).map((section) => {
        const { color, background, Icon } = KIND_STYLE[section.kind];

        return (
          <Box component="section" key={section.title} id={`changelog-${section.kind}`} sx={{ mb: 3 }}>
            <Typography component="h2" sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 16, fontWeight: 600, mb: 1.25 }}>
              <Box component="span" sx={iconBoxSx(color, background, 30, "8px")}><Icon sx={{ fontSize: 18 }} /></Box>
              {section.title}
              <Box component="span" sx={{ fontSize: 13, fontWeight: 400, color: "text.secondary" }}>{section.items.length}</Box>
            </Typography>
            <Box component="table" sx={tableSx}>
              <thead>
                <tr>
                  <th>{t(CustomerMessages.docsWhatChanged)}</th>
                  <th style={{ width: 110 }} aria-label={t(CustomerMessages.docsDetails)} />
                </tr>
              </thead>
              <tbody>
                {section.items.map((item) => (
                  <tr key={`${item.title}-${item.text.slice(0, 30)}`} onClick={() => setOpen({ kind: section.kind, item })}>
                    <td style={{ borderLeft: `3px solid ${color}` }}>
                      {item.title && <Box component="span" sx={{ fontWeight: 600 }}>{renderInline(item.title)}</Box>}
                      <Box component="span" sx={{ display: "block", fontSize: 13.5, color: "text.secondary", mt: 0.25 }}>{summarize(item.text)}</Box>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <Button
                        size="small"
                        sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                        endIcon={<ChevronRightIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpen({ kind: section.kind, item });
                        }}
                      >
                        {t(CustomerMessages.docsDetails)}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Box>
          </Box>
        );
      })}

      <Dialog open={!!open} onClose={() => setOpen(null)} maxWidth="sm" fullWidth aria-labelledby="changelog-item-title">
        {open && (() => {
          // long texts read better split: the first sentence as the lead, the rest as checked points
          const { color, background, Icon, label } = KIND_STYLE[open.kind];
          const [lead, ...points] = splitSentences(open.item.text);

          return (
            <>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, pt: 2, pr: 1, pb: 1, pl: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Box component="span" sx={chipSx(color, background)}>
                    <Icon sx={{ fontSize: 14 }} />
                    {t(label)}
                  </Box>
                  <Typography id="changelog-item-title" sx={{ fontSize: 18, fontWeight: 600, lineHeight: 1.3, mt: 1 }}>
                    {open.item.title ? renderInline(open.item.title) : versionLabel}
                  </Typography>
                </Box>
                <IconButton aria-label={t(CustomerMessages.docsClose)} onClick={() => setOpen(null)}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <DialogContent sx={{ "& code": { fontSize: 13, px: 0.5, borderRadius: "4px", backgroundColor: "action.hover" } }}>
                <Box sx={leadSx(color, background)}>
                  {renderInline(lead || "")}
                </Box>
                {points.length > 0 && (
                  <Box component="ul" sx={{ listStyle: "none", m: 0, mt: 2, p: 0, fontSize: 14.5, lineHeight: 1.55 }}>
                    {points.map((point) => (
                      <Box component="li" key={point.slice(0, 60)} sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", "& + li": { mt: 1 } }}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 19, mt: "1px", flex: "none", color }} />
                        <span>{renderInline(point)}</span>
                      </Box>
                    ))}
                  </Box>
                )}
                {open.item.details.length > 0 && (
                  <Box sx={{ display: "grid", gap: 1, mt: 2 }}>
                    {open.item.details.map((detail, position) => (
                      <Box key={detail.slice(0, 60)} sx={stepSx}>
                        <Box component="span" sx={{ ...iconBoxSx(color, background, 22, "50%"), fontSize: 12, fontWeight: 700 }}>{position + 1}</Box>
                        <span>{renderInline(detail)}</span>
                      </Box>
                    ))}
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <Typography variant="caption" color="textSecondary" sx={{ flex: 1, pl: 2 }}>
                  {`${versionLabel}${entry.date ? ` · ${entry.date}` : ""}`}
                </Typography>
                <Button onClick={() => setOpen(null)}>{t(CustomerMessages.docsClose)}</Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
    </div>
  );
};

ChangelogVersionView.defaultProps = {
  actions: undefined
};

export default ChangelogVersionView;
